import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import {
  transformTimeboxPrefToDB,
  transformTaskPrefForEndpoint,
  transformRoutineForEndpoint,
  transformWorkHourPrefToDB,
} from '../../components';
import {
  UseDailyPrefSchedulerChatProps,
  UseDailyPrefSchedulerChatReturn,
  SchedulerChatMessage,
  AgentMessageId,
} from './types';
import { validateScheduleAvailability, getPreferenceSummary } from './utils';
import { generateChatMessages } from './messageGenerator';
import { usePreferenceManagement } from './usePreferenceManagement';
import { isEmpty } from 'lodash';
import { SchedulerPreferenceV3 } from '@demind-inc/core';
import { useGeneralSettings } from '../useGeneralSettings';
import {
  FirestoreRoutineActivity,
  useCalendarEventsCache,
  useAuthContext,
} from '../../data-access';
import { useFeatureFlag } from '../../utils';
import { useTaskActions } from '../../data-access';

export const useDailyPrefSchedulerChat = ({
  selectedDate,
  rangeEndDate,
  schedulerScope = 'day',
  hasTodoInited: _hasTodoInited = false,
  handleRunAIScheduler,
  onScheduleForTomorrow,
}: UseDailyPrefSchedulerChatProps): UseDailyPrefSchedulerChatReturn => {
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const {
    featureFlags: { FF_ai_scheduler_chat_v5 },
  } = useFeatureFlag();
  const { user } = useAuthContext();
  const { findInCacheByDate } = useCalendarEventsCache();

  const [visibleMessages, setVisibleMessages] = useState<SchedulerChatMessage[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [customRuleInput, setCustomRuleInput] = useState('');
  const processedActivityIdsRef = useRef<Set<string>>(new Set());

  const {
    timeBoxPrefItems,
    routinePrefItem,
    tasksPrefItems,
    workHourPrefItem,
    isRoutineLoading,
    isPreferenceLoading,
    isTasksLoading,
    schedulerPreferences,
    dispatchTimeBoxPrefItems,
    dispatchRoutinePrefItem,
    dispatchTasksPrefItems,
    dispatchWorkHourPrefItem,
    updatePreferences,
    handleAddRoutineActivity,
    handleDeleteRoutine,
  } = usePreferenceManagement(selectedDate, schedulerScope, rangeEndDate);
  const { updateTodoTask } = useTaskActions();

  // Keep in sync with reducer/query readiness — do not defer via useEffect, or the
  // Plan My Day CTA can enable for a frame with still-empty todo/routine prefs.
  const isInitializing = isRoutineLoading || isPreferenceLoading || isTasksLoading;

  // Fetch routine events from cache for the selected date
  const existingRoutineEvents = useMemo(() => {
    if (!user.userId || !selectedDate) return [];

    const cachedEvents = findInCacheByDate(selectedDate.format('YYYY-MM-DD'), user.userId) ?? [];
    return cachedEvents.filter((event) => {
      return (
        ['routine', 'meal'].includes(event.autoScheduleFrom?.from) && event.status !== 'cancelled'
      );
    });
  }, [findInCacheByDate, selectedDate, user.userId]);

  // Create a set of scheduled routine activity IDs for quick lookup
  const scheduledRoutineActivityIds = useMemo(() => {
    return new Set(
      existingRoutineEvents
        .map((event) => event.autoScheduleFrom?.id)
        .filter((id): id is string => !!id)
    );
  }, [existingRoutineEvents]);

  // Reset processed activities when selected date changes
  useEffect(() => {
    processedActivityIdsRef.current.clear();
  }, [selectedDate]);

  // Automatically uncheck routine activities that are already scheduled
  useEffect(() => {
    Object.entries(routinePrefItem ?? {}).forEach(([activityId, activity]) => {
      const isAlreadyScheduled = scheduledRoutineActivityIds.has(activityId);

      // Only process each activity once
      if (isAlreadyScheduled && !processedActivityIdsRef.current.has(activityId)) {
        processedActivityIdsRef.current.add(activityId);
        dispatchRoutinePrefItem({ type: 'uncheck', activityId });
      }
    });
  }, [scheduledRoutineActivityIds, routinePrefItem, dispatchRoutinePrefItem]);

  const hasSetDefaultPrefs = useMemo(() => !isEmpty(schedulerPreferences), [schedulerPreferences]);

  const { canSchedule, noScheduleReason } = useMemo(() => {
    return validateScheduleAvailability(schedulerPreferences, selectedDate);
  }, [schedulerPreferences, selectedDate]);

  const handleTaskChange = useCallback(
    (id: string, pref: any) => {
      dispatchTasksPrefItems({ type: 'update', taskId: id, payload: pref });

      if (pref.boardId && id && !isEmpty(pref)) {
        updateTodoTask({
          boardId: pref.boardId,
          taskId: id,
          newTaskInfo: pref,
          showSnackbar: false,
        });
      }
    },
    [dispatchTasksPrefItems, updateTodoTask]
  );

  const handleTaskSelection = useCallback(
    (id: string, checked: boolean) => {
      dispatchTasksPrefItems({
        type: checked ? 'check' : 'uncheck',
        payload: { selected: checked },
        taskId: id,
      });
    },
    [dispatchTasksPrefItems]
  );

  const handleRoutineSelection = useCallback(
    (id: string, checked: boolean) => {
      dispatchRoutinePrefItem({ type: checked ? 'check' : 'uncheck', activityId: id });
    },
    [dispatchRoutinePrefItem]
  );

  const handleRoutineChange = useCallback(
    (id: string, pref: FirestoreRoutineActivity) => {
      dispatchRoutinePrefItem({ type: 'update', activityId: id, payload: pref });
    },
    [dispatchRoutinePrefItem]
  );

  const handleRoutineAddCb = useCallback(
    (pref: FirestoreRoutineActivity) => {
      handleAddRoutineActivity(pref);
    },
    [handleAddRoutineActivity]
  );

  const handleRoutineDeleteCb = useCallback(
    (id: string) => {
      handleDeleteRoutine(id);
    },
    [handleDeleteRoutine]
  );

  const handleRun = useCallback(
    async (userMessage?: string) => {
      const transformedPref: SchedulerPreferenceV3 = {
        ...transformTimeboxPrefToDB(timeBoxPrefItems),
        ...transformWorkHourPrefToDB(workHourPrefItem),
      };
      const transformedRoutine = transformRoutineForEndpoint(
        routinePrefItem,
        selectedDate,
        schedulerScope === 'week' ? rangeEndDate : undefined
      );
      const transformedTasks = transformTaskPrefForEndpoint(
        tasksPrefItems,
        (schedulerScope === 'week' && rangeEndDate ? rangeEndDate : selectedDate).format('YYYY-MM-DD')
      );

      const summary = await handleRunAIScheduler(
        transformedPref,
        selectedDate.format('YYYY-MM-DD'),
        transformedTasks,
        transformedRoutine,
        userMessage,
        schedulerScope,
        rangeEndDate?.format('YYYY-MM-DD')
      );
      if (!hasSetDefaultPrefs) {
        // If user has not set default prefs, update them
        updatePreferences(transformedPref);
      }
      return summary;
    },
    [
      timeBoxPrefItems,
      workHourPrefItem,
      tasksPrefItems,
      routinePrefItem,
      selectedDate,
      rangeEndDate,
      hasSetDefaultPrefs,
      handleRunAIScheduler,
      updatePreferences,
      schedulerScope,
    ]
  );

  // Create chat messages (sessions + optional custom rule)
  const chatMessages: SchedulerChatMessage[] = useMemo(() => {
    return generateChatMessages({
      canSchedule,
      enableCustomRuleInput: true,
      schedulerScope,
    });
  }, [canSchedule, schedulerScope]);

  // Show first message(s) immediately on mount. Week scope opens with a
  // user "Plan my week" turn ahead of the welcome AI bubbles (same pattern
  // as plan-my-day's intro user bubble).
  useEffect(() => {
    if (chatMessages.length === 0) return;

    if (visibleMessages.length === 0) {
      setCustomRuleInput('');
      setIsTyping(true);
      const timer = setTimeout(() => {
        const initialCount = schedulerScope === 'week' && chatMessages.length >= 2 ? 2 : 1;
        setVisibleMessages(chatMessages.slice(0, initialCount));
        setCurrentMessageIndex(initialCount);
        setIsTyping(false);
      }, 100); // Small delay to ensure proper rendering

      return () => clearTimeout(timer);
    }
  }, [chatMessages, schedulerScope, visibleMessages.length]);

  useEffect(() => {
    setCustomRuleInput('');
    setVisibleMessages([]);
    setCurrentMessageIndex(0);
  }, []);

  // Update visible messages when timebox, tasks, or routine preferences change
  useEffect(() => {
    if (chatMessages.length === 0 || visibleMessages.length === 0) return;
    // Update visible messages with the latest chatMessages
    setVisibleMessages((prev) => {
      const updatedMessages = prev.map((visibleMessage) => {
        // Find the corresponding message in chatMessages
        const updatedMessage = chatMessages.find((msg) => msg.id === visibleMessage.id);
        return updatedMessage || visibleMessage;
      });
      return updatedMessages;
    });
  }, [
    JSON.stringify(timeBoxPrefItems),
    JSON.stringify(tasksPrefItems),
    JSON.stringify(routinePrefItem),
    JSON.stringify(workHourPrefItem),
    currentMessageIndex,
  ]);

  const handleNextMessage = () => {
    // Get the current message that the user is confirming
    const currentMessage = chatMessages[currentMessageIndex - 1]; // -1 because currentMessageIndex is already incremented

    if (currentMessage) {
      const userResponse =
        getPreferenceSummary(
          currentMessage.id as AgentMessageId,
          workHourPrefItem,
          timeBoxPrefItems,
          tasksPrefItems,
          routinePrefItem,
          timeFormat
        ) || 'Next';

      // Add user response message
      const userMessage: SchedulerChatMessage = {
        id: `user-${currentMessage.id}`,
        type: 'user',
        content: userResponse,
        timestamp: new Date(),
      };

      setVisibleMessages((prev) => [...prev, userMessage]);

      setIsTyping(true);
      // Show next message after a delay
      setTimeout(() => {
        setIsTyping(false);

        if (currentMessageIndex < chatMessages.length) {
          setVisibleMessages((prev) => [...prev, chatMessages[currentMessageIndex]]);
          setCurrentMessageIndex((prev) => prev + 1);
        }
      }, 1000);
    }
  };

  const handleCustomRuleInput = (text: string) => {
    // Add user response message
    const userMessage: SchedulerChatMessage = {
      id: 'user-custom-rule',
      type: 'user',
      content: text,
      timestamp: new Date(),
    };

    setCustomRuleInput(text);
    setVisibleMessages((prev) => {
      const filteredMessages = prev.filter((message) => message.id !== userMessage.id);
      return [...filteredMessages, userMessage];
    });

    setIsTyping(true);
    // Show next message after a delay
    setTimeout(() => {
      setIsTyping(false);

      if (currentMessageIndex < chatMessages.length) {
        setVisibleMessages((prev) => [...prev, chatMessages[currentMessageIndex]]);
        setCurrentMessageIndex((prev) => prev + 1);
      }
    }, 1000);
  };

  return {
    handleCustomRuleInput,
    visibleMessages,
    isTyping,
    handleNextMessage,
    timeBoxPrefItems,
    tasksPrefItems,
    routinePrefItem,
    workHourPrefItem,
    isInitializing,
    canSchedule,
    isPreferenceLoading: isPreferenceLoading || isTasksLoading, // Combine preference and task loading states
    isRoutineLoading,
    onTaskSelection: handleTaskSelection,
    onRoutineSelection: handleRoutineSelection,
    onTaskChange: handleTaskChange,
    onRoutineChange: handleRoutineChange,
    onRoutineAdd: handleRoutineAddCb,
    onRoutineDelete: handleRoutineDeleteCb,
    onWorkHourChange: (pref: typeof workHourPrefItem) => {
      dispatchWorkHourPrefItem({ type: 'workTime', payload: pref });
      const workHours = transformWorkHourPrefToDB(pref);
      updatePreferences(workHours);
    },
    noScheduleReason,
    handleRun,
    currentMessageIndex,
    totalMessages: chatMessages.length,
    isV5Flow: true,
    existingRoutineEvents,
    scheduledRoutineActivityIds,
  };
};
